import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { useDrawingArea, useSvgRef } from "@mui/x-charts/hooks"
import type { DateTime } from "luxon"

import type { EntityType } from "types/common.types"

import useDateRange from "helpers/hooks/useDateRange"
import useEventListeners from "helpers/hooks/useEventListeners"
import { setZoomDateRange } from "widgets/dates/helpers"
import type { SvgPoint } from "./helpers"
import useDragZoom from "./useDragZoom"
import useTouchSpanZoom from "./useTouchSpanZoom"

interface MuiChartZoomProps {
  entity: EntityType
  xDomainMin: number
  xDomainMax: number
}

/**
 * Range-select zoom overlay: previews a time window while the gesture is live
 * and, on release, rewrites the global date range (which re-queries the data).
 *
 * Two gestures, one per hook, split by `pointerType` rather than by viewport
 * width so a device with both a mouse and a touchscreen behaves correctly for
 * each input. This file only wires them up; each hook owns its own state
 * machine and the band it wants drawn.
 *
 * Zooming out is not a gesture: ExitZoomModeButton restores the previous range.
 */
const MuiChartZoom = ({ entity, xDomainMin, xDomainMax }: MuiChartZoomProps) => {
  const { getDateRangeTools } = useDateRange()
  const { dispatchDateRange, dateRange } = useMemo(
    () => getDateRangeTools(entity),
    [getDateRangeTools, entity],
  )
  const [_searchParams, setSearchParams] = useSearchParams()
  const dispatchCombo = useMemo(() => {
    return { setSearchParams, dispatchDateRange }
  }, [dispatchDateRange, setSearchParams])

  const svgRef = useSvgRef()
  const drawingArea = useDrawingArea()

  const getSvgPoint = useCallback(
    (e: PointerEvent): SvgPoint | null => {
      const svg = svgRef.current
      if (!svg) return null

      const rect = svg.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    },
    [svgRef],
  )

  const commitRange = useCallback(
    (start: DateTime, end: DateTime) => {
      setZoomDateRange(start, end, dateRange, dispatchCombo)
    },
    [dateRange, dispatchCombo],
  )

  const drag = useDragZoom({
    svgRef,
    drawingArea,
    getSvgPoint,
    commitRange,
    xDomainMin,
    xDomainMax,
  })
  const touch = useTouchSpanZoom({
    drawingArea,
    getSvgPoint,
    commitRange,
    xDomainMin,
    xDomainMax,
  })

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === "touch") {
        touch.onPointerDown(e)
        return
      }
      drag.onPointerDown(e)
    },
    [touch, drag],
  )

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === "touch") {
        touch.onPointerMove(e)
        return
      }
      drag.onPointerMove(e)
    },
    [touch, drag],
  )

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === "touch") {
        touch.onPointerUp(e)
        return
      }
      drag.onPointerUp(e)
    },
    [touch, drag],
  )

  // A cancel means the browser took the gesture over, typically to scroll the
  // page. Each hook filters by its own pointer, so a cancel from elsewhere on
  // the page cannot kill a live chart gesture.
  const handlePointerCancel = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === "touch") {
        touch.onPointerCancel(e)
        return
      }
      drag.onPointerCancel(e)
    },
    [touch, drag],
  )

  // Both gestures have to go on an interrupt: it swallows the pointerup they
  // rely on, and a drag left behind keeps its band on screen and commits its
  // stale range on the next click, whose pointerid still matches the abandoned one.
  const handleInterrupt = useCallback(() => {
    drag.cancel()
    touch.abort()
  }, [drag, touch])

  // Only pointerdown is bound to the chart; the rest live on window so a gesture
  // survives the pointer leaving the SVG.
  //
  // `touch-action` is deliberately left alone: ChartsSurface already sets it to
  // `pan-y` on this same SVG, which is exactly what we need — vertical scrolling
  // stays with the browser while horizontal and two-finger gestures reach us.
  useEventListeners(svgRef, { pointerdown: handlePointerDown })
  useEventListeners(window, {
    pointermove: handlePointerMove,
    pointerup: handlePointerUp,
    pointercancel: handlePointerCancel,
    contextmenu: handleInterrupt,
    blur: handleInterrupt,
  })

  // A live drag owns the band: below its threshold it wants none drawn, and
  // falling through to the touch selection would draw someone else's.
  const selection = drag.isDragging ? drag.selection : touch.selection

  if (!selection) return null

  return (
    <rect
      x={selection.x}
      y={drawingArea.top}
      width={selection.width}
      height={drawingArea.height}
      fill="rgba(33, 150, 243, 0.05)"
      stroke="rgba(33, 150, 243, 0.5)"
      strokeWidth={2}
      pointerEvents="none"
    />
  )
}

export default MuiChartZoom
